import { CSSProperties } from "react";
import styled from "styled-components";
import { BarChart as BarChartUI, barChartClassNames } from "@evergis/charts";
import { Blank, LinearProgress } from "@evergis/ui";

import { LEFT_PANEL_MIN_WIDTH } from "../../../../components/Layout/constants";

import { BottomState } from "../../../../ducks/layout/layout";

export const LayerStatisticsContainer = styled(Blank)<{ bottomState?: BottomState; bottomHeight?: number }>`
  position: relative;
  right: 0.75rem;
  top: 2.75rem;
  width: ${`${LEFT_PANEL_MIN_WIDTH}px`};
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: initial;
  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.15);
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};
  max-height: calc(
    100vh - 2.25rem - 1rem - ${({ bottomState, bottomHeight }) => (bottomState === "opened" ? bottomHeight : 0)}px
  );

  ${LinearProgress} {
    position: absolute;
    top: 0;
    left: 0;
    min-height: 0.25rem;
    max-height: 0.25rem;
    border-radius: ${({ theme: { borderRadius } }) => `${borderRadius.xxLarge} ${borderRadius.xxLarge} 0 0`};
    overflow: hidden;
  }

  @media screen and (max-width: 100.75rem) {
    max-height: calc(100vh - 2.25rem - 4.5rem - ${({ bottomState }) => (bottomState === "opened" ? 350 : 0)}px);
  }
`;

export const LayerStatisticsHead = styled.div`
  padding: 1.5rem 1rem 1rem 1.5rem;
  box-sizing: border-box;
`;

export const LayerStatisticsTitle = styled.span`
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

export const LayerStatisticsBody = styled.div`
  padding: 1.5rem 0.5rem 1.5rem 1.5rem;
  max-height: 100%;
  overflow-y: scroll;
  box-sizing: border-box;
`;

export const LayerStatisticsPickAttributesMenu = styled.div`
  display: flex;
  flex-direction: column;
  position: inherit;
  padding: 1rem 1rem 0.25rem;
  width: 17.5rem;
  box-sizing: border-box;
`;

export const LayerStatisticsAttributeMenu = styled.div`
  display: flex;
  flex-direction: column;
  position: inherit;
  box-sizing: border-box;
`;

export const LayerStatisticsAttributeTitle = styled.div`
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

export const LayerStatisticsAttributeDescription = styled.div`
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  opacity: 0.54;
`;

export const LayerStatisticsAttributeValue = styled.div`
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -0.00625rem;
`;

export const BarChart = styled(BarChartUI)`
  .${barChartClassNames.barChartXAxis} {
    .domain,
    line {
      display: none;
    }

    .tick {
      text {
        text-anchor: start;
        font-size: 12px;
        color: rgba(48, 69, 79, 0.46);
      }

      :last-of-type {
        text {
          text-anchor: end;
        }
      }
    }
  }

  .marker {
    font-size: 12px;
  }
`;

export const TooltipContainer = styled.div`
  position: relative;
  font-size: 0.75rem;
  color: #ffffff;
  margin-bottom: 0.5rem;
  padding: 0.375rem;
  background-color: rgba(0, 0, 0, 1);
  border-radius: 0.25rem;
  box-shadow: 0 0.1875rem 0.5rem rgba(48, 69, 79, 0.06);

  :before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translate(-50%, 100%);
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0.25rem 0.1875rem 0 0.1875rem;
    border-color: rgba(48, 69, 79, 1) transparent transparent transparent;
  }
`;

export const TooltipDescription = styled.div`
  opacity: 0.54;
  margin-top: 0.125rem;
`;

export const VisibilityCheckboxLabel = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const LayerStatisticsValuePlaceholder = styled.div`
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.small};
  background: ${({ theme: { palette } }) => palette.elementDark};
  height: 0.75rem;
  width: 100%;
`;

export const PlaceholderShining = styled.div<{ height?: CSSProperties["height"] }>`
  position: absolute;
  top: 0;
  left: 0;
  background-color: ${({ theme: { palette } }) => palette.background};
  width: 100%;
  height: ${({ height }) => height};
  animation-duration: 0.6s;
  animation-iteration-count: infinite;
  animation-name: loader-animate;
  animation-timing-function: linear;
  background: -webkit-linear-gradient(
    left,
    ${({ theme: { palette } }) => palette.elementTransparent} 0%,
    ${({ theme: { palette } }) => palette.background} 30%,
    ${({ theme: { palette } }) => palette.elementTransparent} 81%
  );
  background: -o-linear-gradient(
    left,
    ${({ theme: { palette } }) => palette.elementTransparent} 0%,
    ${({ theme: { palette } }) => palette.background} 30%,
    ${({ theme: { palette } }) => palette.elementTransparent} 81%
  );
  background: linear-gradient(
    to right,
    ${({ theme: { palette } }) => palette.elementTransparent} 0%,
    ${({ theme: { palette } }) => palette.background} 30%,
    ${({ theme: { palette } }) => palette.elementTransparent} 81%
  );

  @keyframes loader-animate {
    0% {
      transform: translate3d(-100%, 0, 0);
    }
    100% {
      transform: translate3d(100%, 0, 0);
    }
  }
`;
